import React, { useState, useCallback, useEffect } from "react";
import { withFormik } from 'formik';
import * as Yup from "yup";
import moment from "moment"
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';

import { Button, Typography, Spinner, Modal, Link, IconButton } from "stories/components";
import { Container, Row, Col, FormContainer, Card, CardBody } from "stories/layout";
import { Input, SelectMulti, Toggle, Radio } from "stories/forms";
import { useNotification } from "stories/components/Notification"
import CurrencySelect from "components/Forms/CurrencySelect/CurrencySelect"
import SectorSelect from "components/Forms/SectorSelect/SectorSelect"
import TagSelect from "components/Forms/TagSelect/TagSelect"
import SectorEdit from "views/app/SectorEdit/SectorEdit"
import TagEdit from "views/app/TagEdit/TagEdit"

import InvestmentTypeSelectSub from "components/Forms/InvestmentTypeSelectSub/InvestmentTypeSelectSub"
import TaxTreatmentSelect from "components/Forms/TaxTreatmentSelect/TaxTreatmentSelect"
import {
  buildFundPayload,
  buildFundValidationSchema,
  buildFundErrors,
  FUND_DEFAULT_VALUES,
  FUND_DIM_TEXT_MAX_LENGTH,
  labelWithCurrency,
  showVintageYear,
} from "./fundPayload"
import { useAppDialog } from "components/AppDialog/useAppDialog"
import PriceSourceSection from "components/PriceSource/PriceSourceSection"

import { useAppContext } from "contexts/AppContext"


import { 
  httpGetPrivateInvestmentFund, 
  httpCreatePrivateInvestmentFund, 
  httpUpdatePrivateInvestmentFund, 
  httpDeletePrivateInvestmentFund 
} from "services/private_investment_funds"
import {
  httpGetPortfolioSecurityDimensions,
  httpCreatePortfolioSecurityDimension,
  httpUpdatePortfolioSecurityDimension,
  httpDeletePortfolioSecurityDimension
} from "services/accounts"
import { set } from "date-fns";

const defaultValues = FUND_DEFAULT_VALUES

const PrivateInvestmentFundEdit = (props) => {
  const { dialog, confirm } = useAppDialog();

  
  const {
     values,
     setValues,
     touched,
     setTouched,
     errors,
     setErrors,
     handleChange,
     handleBlur,
     handleSubmit,
     setFieldValue,
     isTouched,
     isSubmitting,
     validateForm,
     preSetValues
   } = props;
   
   const {
      t,
      history,
      objectId,
      selectedSector,
      setSelectedSector,
      portfolioSecurityDimensions,
      loadPortfolioSecurityDimensions,
   } = props;

   
   const getFormikInputProps = useCallback(
    name => ({
      name,
      value: values[name],
      error: Boolean(touched[name] && errors[name]),
      errorText: touched[name] ? errors[name] : "",
      onBlur: handleBlur,
      onChange: handleChange
    }),
    [values, errors, touched, handleBlur, handleChange]
  );

  // Text fields of the reporting section: the server returns null for an empty value.
  const getDimTextProps = (name) => ({
    ...getFormikInputProps(name),
    value: values?.[name] ?? "",
    maxLength: FUND_DIM_TEXT_MAX_LENGTH[name],
  });
  
  const account = useStoreState((state) => state.account?.account);
  const getSectors = useStoreActions((actions) => actions.sectors.getSectors);
  const getTags = useStoreActions((actions) => actions.tags.getTags);
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);

  // Portfolio Security Dimension state
  const [newPortfolioDimension, setNewPortfolioDimension] = useState({ portfolio: null, dimension: '' });
  const [savingPortfolioDimension, setSavingPortfolioDimension] = useState(false);

  const handleAddPortfolioDimension = async () => {
    if (!newPortfolioDimension.portfolio || !newPortfolioDimension.dimension || !objectId) {
      return;
    }
    setSavingPortfolioDimension(true);
    try {
      await httpCreatePortfolioSecurityDimension(account.id, {
        portfolio: newPortfolioDimension.portfolio,
        related_model_type: 30, // PrivateInvestmentFund
        related_model_id: objectId,
        dimension: newPortfolioDimension.dimension
      });
      setNewPortfolioDimension({ portfolio: null, dimension: '' });
      loadPortfolioSecurityDimensions();
      props.notify({ title: '', message: t("Salkkudimensio lisätty") });
    } catch (error) {
      console.error("Error adding portfolio dimension:", error);
      props.notify({ title: t("Virhe"), message: error?.data?.detail || t("Virhe salkkudimension lisäämisessä") });
    } finally {
      setSavingPortfolioDimension(false);
    }
  };

  const handleUpdatePortfolioDimension = async (item, newDimension) => {
    try {
      await httpUpdatePortfolioSecurityDimension(account.id, {
        portfolio: item.portfolio,
        related_model_type: 30,
        related_model_id: objectId,
        dimension: newDimension
      });
      loadPortfolioSecurityDimensions();
      props.notify({ title: '', message: t("Salkkudimensio päivitetty") });
    } catch (error) {
      console.error("Error updating portfolio dimension:", error);
      props.notify({ title: t("Virhe"), message: error?.data?.detail || t("Virhe salkkudimension päivittämisessä") });
    }
  };

  const handleDeletePortfolioDimension = async (item) => {
    try {
      await httpDeletePortfolioSecurityDimension(account.id, { id: item.id });
      loadPortfolioSecurityDimensions();
      props.notify({ title: '', message: t("Salkkudimensio poistettu") });
    } catch (error) {
      console.error("Error deleting portfolio dimension:", error);
      props.notify({ title: t("Virhe"), message: error?.data?.detail || t("Virhe salkkudimension poistamisessa") });
    }
  };

  const getPortfolioName = (portfolioId) => {
    const portfolio = portfolios?.find(p => p.id === portfolioId);
    return portfolio?.name || portfolioId;
  };

  const getAvailablePortfolios = () => {
    const usedPortfolioIds = portfolioSecurityDimensions?.map(d => d.portfolio) || [];
    return portfolios?.filter(p => !usedPortfolioIds.includes(p.id)) || [];
  };


    // Modal: New/Edit Sector

    const [modalEditOpen, setModalEditOpen] = useState(false)
    const [modalEditId, setModalEditId] = useState()
    
    const toggleModalEdit = () => {
      setModalEditOpen(s => !s);
    }
    
    const openModalEdit = (id) => {
      setModalEditOpen(true)
      setModalEditId(id);
    }
    
    const modalEditResolve = () => {
      setModalEditOpen(false);
      setModalEditId(null);
      getSectors()
    }
    
    const modalEditReject = () => {
      setModalEditOpen(false);
      setModalEditId(null);
    }

      // Modal: New/Edit Tag

      const [modalTagEditOpen, setModalTagEditOpen] = useState(false)
      const [modalTagEditId, setModalTagEditId] = useState()
      
      const toggleModalTagEdit = () => {
        setModalTagEditOpen(s => !s);
      }
      
      const openModalTagEdit = (id) => {
        setModalTagEditOpen(true)
        setModalTagEditId(id);
      }
      
      const modalTagEditResolve = () => {
        setModalTagEditOpen(false);
        setModalTagEditId(null);
        getTags()
      }
      
      const modalTagEditReject = () => {
        setModalTagEditOpen(false);
        setModalTagEditId(null);
      }
  

  


  const onSubmit = (e) => {
    e.preventDefault();
    validateForm().then(res => {
      if (Object.keys(res).length === 0) {
        handleSubmit()
      }
      else {
        setTouched(res);
        setErrors({
          ...res,
          general: t("Please check the form errors", "Tarkista lomakkeen virheet")
        })
      }
    })
  }
  
  const handleCancel = () => {
    setModalEditOpen(false);
    setModalTagEditOpen(false);
    props.handleReject()
  }
  
  const handleDelete = () => {
    confirm(t("are_you_sure_window_confirm","Oletko varma?")).then((result) => {
      if (result) {
        httpDeletePrivateInvestmentFund(objectId).then(() => {
          props.handleResolve()
        })
      }
    });
  }


  const handleSectorChange = (name, value) => {
    setSelectedSector(s => ({
      ...s,
      [name]: value
    }))
  }
  

  const getSectorSelect = useCallback(() => {
    return (
      <SectorSelect
        {...getFormikInputProps("sector_select")}
        label={`${t("sector_title", "Toimiala")}` }
        multiple={false}
        onChange={handleSectorChange}
        value={selectedSector?.sector_select}
        setFieldValue={handleSectorChange}
      />
    )
  }, [selectedSector])


  const getTagSelect = useCallback(() => {
    return (
      <TagSelect
        {...getFormikInputProps("tag")}
        label={`${t("tag_title", "Kategoria")}` }
        multiple={false}
        onChange={setFieldValue}
        value={values?.tag}
        setFieldValue={setFieldValue}
      />
    )
  }, [values?.tag])

  return (
    <>
    {dialog}
    <div className="px-3 pt-2">
      <Row className="mt-2 mb-2 border-bottom align-items-center">
        <Col xs={12} md={6} className="pb-3 pl-3">
          <Typography variant="h2" className="mb-0">
            {t("PrivateInvestmentFundEdit_title", "Oma arvopaperi")}
          </Typography>
        </Col>
        <Col xs={12} md={6} className="pb-3 pr-3 d-flex justify-content-end align-items-center">
          <IconButton
            aria-label={t("Close", "Sulje")}
            size="lg"
            onClick={handleCancel}
            iconName="times"
            data-dismiss="modal"
          />
        </Col>
      </Row>

      <Row className="pt-2">
        <Col xs={12} md={6}>
          <div className="mb-3">
            <Input
              {...getFormikInputProps("name")}
              label={t("PrivateInvestmentFundEdit_name", "Nimi")}
              required
            />
          </div>
          <div className="mb-3">
            <Input
              {...getFormikInputProps("symbol")}
              label={` ${t("PrivateInvestmentFundEdit_symbol", "Symboli")}${values?.id ? "" : " *"}`}
            />
          </div>
          <div className="mb-3">
            <Input
              {...getFormikInputProps("stock_market")}
              label={` ${t("PrivateInvestmentFundEdit_stock_market", "Pörssi")}` }
            />
          </div>
          <div className="mb-3">
            <Input
              {...getFormikInputProps("isin")}
              label={` ${t("PrivateInvestmentFundEdit_isin", "ISIN")}` }
            />
          </div>
          <div className="mb-3">
            <InvestmentTypeSelectSub
              label={`${t("PrivateInvestmentFundEdit_type", "Tyyppi")} *`}
              {...getFormikInputProps("type")}
              required
            />
          </div>
          <div className="mb-3">
            <TaxTreatmentSelect
              {...getFormikInputProps("tax_treatment")}
            />
          </div>

          {values?.type === "listed_stock" && (
            <PriceSourceSection
              objectId={objectId}
              values={values}
              onFundUpdated={(fund) => {
                // Binding rewrites the security from the chosen listing. Without this the
                // form would still hold the pre-binding values and Tallenna would write
                // them straight back over the listing's name, symbol, market and currency.
                if (!fund) return;
                setFieldValue("name", fund.name);
                setFieldValue("symbol", fund.symbol);
                setFieldValue("stock_market", fund.stock_market);
                setFieldValue("currency_label", fund.currency_label);
                setFieldValue("isin", fund.isin);
              }}
            />
          )}
        </Col>

        <Col xs={12} md={6}>
          <div className="mb-1">
            {getSectorSelect()}
          </div>
          <div className="mb-3">
            <button
              type="button"
              className="sk-form-create-link"
              onClick={() => openModalEdit()}
            >
              {t("sector_create_here", "Luo toimiala tästä")}
            </button>
          </div>

          <div className="mb-1">
            {getTagSelect()}
          </div>
          <div className="mb-3">
            <button
              type="button"
              className="sk-form-create-link"
              onClick={() => openModalTagEdit()}
            >
              {t("tag_create_here", "Luo kategoria tästä")}
            </button>
          </div>

          <div className="mb-3">
            <Input
              {...getFormikInputProps("size")}
              label={t("PrivateInvestmentFundEdit_size", "Arvopaperin koko")}
              type="number"
            />
          </div>

          <div className="mb-3">
            <CurrencySelect
              {...getFormikInputProps("currency_label")}
              label={t("PrivateInvestmentFundEdit_currency", "Valuutta")}
              required
              disabled={objectId ? true : false}
            />
            {!objectId && (
              <Typography variant="small">{t("Valuutta tietoa ei voi muokata myöhemmin")}</Typography>
            )}
          </div>

          {account?.show_relation_to_security && (
            <>
              <div className="mb-3">
                <Input
                  {...getFormikInputProps("dimensio")}
                  label={t("rivateInvestmentFundEdit_dimensio", "Dimensio")}
                />
              </div>

              {objectId && (
                <>
                  <div className="mb-1 mt-1">
                    <label className="mb-1 pr-1 header header-bold">{t("account_security_edit_portfolio_dimensions", "Salkkudimensio")}</label>
                    <Typography variant="small" className="text-muted d-block">
                      {t("account_security_edit_portfolio_dimensions_info", "Jos salkulle on määritetty oma dimensio, se yliajaa yllä olevan oletusarvon.")}
                    </Typography>
                  </div>

                  {portfolioSecurityDimensions && portfolioSecurityDimensions.length > 0 && (
                    <div className="mt-2">
                      {portfolioSecurityDimensions.map((item, index) => (
                        <Row key={item.id || index} className="mb-2 align-items-center">
                          <Col sm={12} lg={4}>
                            <Input
                              name={`portfolio_name_${index}`}
                              label=""
                              value={item.portfolio_name || getPortfolioName(item.portfolio)}
                              disabled={true}
                            />
                          </Col>
                          <Col sm={8} lg={5}>
                            <Input
                              name={`portfolio_dimension_${index}`}
                              label=""
                              defaultValue={item.dimension}
                              onBlur={(e) => {
                                if (e.target.value !== item.dimension) {
                                  handleUpdatePortfolioDimension(item, e.target.value);
                                }
                              }}
                            />
                          </Col>
                          <Col sm={4} lg={3}>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeletePortfolioDimension(item)}
                            >
                              {t("Poista")}
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </div>
                  )}

                  {getAvailablePortfolios().length > 0 && (
                    <Row className="mb-2 mt-3 align-items-center">
                      <Col sm={12} lg={4}>
                        <Input
                          type="select"
                          name="new_portfolio_dimension_portfolio"
                          label=""
                          value={newPortfolioDimension.portfolio || ''}
                          onChange={(e) => setNewPortfolioDimension(prev => ({ ...prev, portfolio: parseInt(e.target.value) || null }))}
                        >
                          <option value="">{t("Valitse salkku")}</option>
                          {getAvailablePortfolios().map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </Input>
                      </Col>
                      <Col sm={8} lg={5}>
                        <Input
                          name="new_portfolio_dimension_value"
                          label=""
                          placeholder={t("Dimensio")}
                          value={newPortfolioDimension.dimension}
                          onChange={(e) => setNewPortfolioDimension(prev => ({ ...prev, dimension: e.target.value }))}
                        />
                      </Col>
                      <Col sm={4} lg={3}>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={!newPortfolioDimension.portfolio || !newPortfolioDimension.dimension || savingPortfolioDimension}
                          onClick={handleAddPortfolioDimension}
                        >
                          {savingPortfolioDimension ? t("Tallentaa...") : t("Lisää")}
                        </Button>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </>
          )}
        </Col>
      </Row>

      {account?.show_security_dim_fields && (
        <Row className="pt-2 border-top">
          <Col xs={12}>
            <Typography variant="h4" className="mb-3">
              {t("PrivateInvestmentFundEdit_dim_title", "Lisätiedot (Tableau)")}
            </Typography>
          </Col>
          <Col xs={12} md={6}>
            <div className="mb-3">
              <Input
                {...getDimTextProps("investment_location")}
                label={t("PrivateInvestmentFundEdit_investment_location", "Sijainti")}
              />
            </div>
            <div className="mb-3">
              <Input
                {...getDimTextProps("short_name")}
                label={t("PrivateInvestmentFundEdit_short_name", "Lyhytnimi")}
              />
            </div>
            <div className="mb-3">
              <Input
                {...getDimTextProps("book_entry_account")}
                label={t("PrivateInvestmentFundEdit_book_entry_account", "Arvo-osuustili")}
              />
            </div>
            <div className="mb-3">
              <Input
                {...getDimTextProps("exit_method")}
                label={t("PrivateInvestmentFundEdit_exit_method", "Irtautumistapa")}
              />
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className="mb-3">
              <Input
                {...getFormikInputProps("end_date_override")}
                value={values?.end_date_override ?? ""}
                type="select"
                label={t("PrivateInvestmentFundEdit_end_date_override", "Päättyminen")}
              >
                <option value="">{t("PrivateInvestmentFundEdit_end_date_override_auto", "Päätellään tapahtumista")}</option>
                <option value="no">{t("PrivateInvestmentFundEdit_end_date_override_no", "Ei päättynyt")}</option>
              </Input>
              <Typography variant="small" className="text-muted d-block mt-1">
                {t("PrivateInvestmentFundEdit_end_date_override_info", "Ei päättynyt: sijoitusta ei tulkita päättyneeksi, vaikka määrä olisi nolla.")}
              </Typography>
            </div>
            {showVintageYear(values) && (
              <div className="mb-3">
                <Input
                  {...getFormikInputProps("vintage_year")}
                  value={values?.vintage_year ?? ""}
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  label={t("PrivateInvestmentFundEdit_vintage_year", "Vintage-vuosi")}
                />
              </div>
            )}
            <div className="mb-3">
              <Input
                {...getFormikInputProps("commitment_given")}
                type="number"
                label={labelWithCurrency(t("PrivateInvestmentFundEdit_commitment_given", "Sitoumus"), values?.currency_label)}
              />
            </div>
            <div className="mb-3">
              <div
                className="sk-toggle-field"
                role="group"
                aria-label={t("PrivateInvestmentFundEdit_is_core", "Core-sijoitus")}
              >
                <Toggle
                  id="is_core"
                  name="is_core"
                  checked={Boolean(values?.is_core)}
                  onChange={() => setFieldValue("is_core", !values?.is_core)}
                  labelOn=""
                  labelOff=""
                />
                <button
                  type="button"
                  className="sk-toggle-field__label"
                  onClick={() => setFieldValue("is_core", !values?.is_core)}
                >
                  {t("PrivateInvestmentFundEdit_is_core", "Core-sijoitus")}
                </button>
              </div>
            </div>
            <div className="mb-3">
              <div
                className="sk-toggle-field"
                role="group"
                aria-label={t("PrivateInvestmentFundEdit_in_trading_book", "Kuuluu arvopaperikauppaan")}
              >
                <Toggle
                  id="in_trading_book"
                  name="in_trading_book"
                  checked={values?.in_trading_book !== false}
                  onChange={() => setFieldValue("in_trading_book", values?.in_trading_book === false)}
                  labelOn=""
                  labelOff=""
                />
                <button
                  type="button"
                  className="sk-toggle-field__label"
                  onClick={() => setFieldValue("in_trading_book", values?.in_trading_book === false)}
                >
                  {t("PrivateInvestmentFundEdit_in_trading_book", "Kuuluu arvopaperikauppaan")}
                </button>
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Row className="bg-lighter pt-3 rounded-bottom">
        <Col className="mb-3">
          <Button onClick={handleCancel} variant="secondary" className="sk-modal-dismiss">{t("Close", "Sulje")}</Button>
        </Col>
        <Col className="mb-3 text-right">
          <Button variant="primary" disabled={isSubmitting} onClick={onSubmit}>
            {t("Save", "Tallenna")}
          </Button>
          {Boolean(errors["general"]) && <Typography className="text-danger">{errors["general"]}</Typography>}
        </Col>
      </Row>
    </div>

      <Modal
      title={t("sector_title","Toimiala")}
      isOpen={modalEditOpen}
      toggleModal={toggleModalEdit}
      container="body"
      backdrop={false}
      zIndex={1060}
    >
      <SectorEdit
        objectId={modalEditId}
        handleResolve={modalEditResolve}
        handleReject={modalEditReject}
        extraMargin={true}
      />

    </Modal>
    <Modal
      title={t("tag_title", "Kategoria")}
      isOpen={modalTagEditOpen}
      toggleModal={toggleModalTagEdit}
      container="body"
      backdrop={false}
      zIndex={1070}
    >

            <TagEdit
        objectId={modalTagEditId}
        handleResolve={modalTagEditResolve}
        handleReject={modalTagEditReject}
        extraMargin={true}
      />

    </Modal>
        
    </>
  );
}

const PrivateInvestmentFundEditFormik = withFormik({
    enableReinitialize: true,
    validateOnMount: true,
    mapPropsToValues: props => {
      const { preSetValues } = props;
      if ( preSetValues) {
        return preSetValues;
      } else {
        return {
          ...defaultValues
        }
      }
    },
    validationSchema: props => {
      const {t} = props;
      const required = t("The field is required");
      return buildFundValidationSchema(required, t);
    },
    handleSubmit: (values, { setSubmitting, setErrors, props }) => {
      const {t, portfolio, account, selectedSector} = props;
      
      let data = buildFundPayload(values, { account, selectedSector });
      
      if (values?.id) {
        httpUpdatePrivateInvestmentFund(values.id, data).then(res => {
          setSubmitting(false);
          props.notify({ title:t("Private investment fund", "Arvopapri"), message:t("Updated")})
          props.handleResolve()
          
        }, error => {
          setSubmitting(false);
          if (error?.data?.detail) {
            setErrors({
              general: error?.data?.detail,
              ...buildFundErrors(error?.data, t),
            })
          }
          else if (error?.data) {
            setErrors({
              general: t(error?.data),
              ...buildFundErrors(error?.data, t),
            })
          }
          else {
            setErrors({
              general: t("Unknown error. Please try again later or contact support.")
            })
          }
        })
      }
      else {
        httpCreatePrivateInvestmentFund(data).then(res => {
          setSubmitting(false);
          // A listed stock with no price source shows its acquisition price as if it were a
          // market value, so say out loud that one step is still missing.
          if (data?.type === "listed_stock") {
            props.notify({
              title: t("Private investment fund", "Arvopaperi"),
              message: t("price_source_created_needs_source",
                "Arvopaperi luotu. Avaa se vielä ja valitse kurssilähde, niin kurssit päivittyvät automaattisesti."),
            })
          }
          else {
            props.notify({ title:t("Private investment fund", "Arvopaperi"), message:t("Created")})
          }
          props.handleResolve()
          
        }, error => {
          setSubmitting(false);
          if (error?.data?.detail) {
            setErrors({
              general: error?.data?.detail,
              ...buildFundErrors(error?.data, t),
            })
          }
          else if (error?.data) {
            setErrors({
              general: t(error?.data),
              ...buildFundErrors(error?.data, t),
            })
          }
          else {
            setErrors({
              general: t("Unknown error. Please try again later or contact support.")
            })
          }
        })
      }
      
      
    },
    displayName: "BasicForm"
  
})(PrivateInvestmentFundEdit)
    
    
const PrivateInvestmentFundEditView = ({history, objectId, modalOpen, handleReject, ...rest}) => {
  // API requests here
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [preSetValues, setPreSetValues] = useState();
  const { notify } = useNotification(); // import { useNotification } from "stories/components/Notification"
  const account = useStoreState((state) => state.account?.account);
  const [selectedSector, setSelectedSector] = useState({sector_select: []});
  const { getSectorName } = useAppContext()
  const tags = useStoreState((state) => state.tags?.tags);
  const [portfolioSecurityDimensions, setPortfolioSecurityDimensions] = useState([]);

  const loadPortfolioSecurityDimensions = useCallback(() => {
    if (account?.id && objectId) {
      httpGetPortfolioSecurityDimensions(account.id, {
        related_model_type: 30, // PrivateInvestmentFund
        related_model_id: objectId
      }).then(res => {
        setPortfolioSecurityDimensions(res?.data || []);
      }).catch(err => {
        console.error("Error loading portfolio security dimensions:", err);
        setPortfolioSecurityDimensions([]);
      });
    }
  }, [account?.id, objectId]);

  
  const getObject = (id) => {
    
    
    setLoading(true)
    
    httpGetPrivateInvestmentFund(id).then(res => {
      const tag_id = res?.data?.tag
      const tag = tags?.find(obj => obj.id === tag_id)
      
      setPreSetValues({
        ...defaultValues,
        ...res?.data,
        tag: tag ? [{id: tag_id, label: tag?.name, name:tag?.name }] : []
      });
      let sector = null
      if (res?.data?.private_sector){
        //add 1_ to private_sector id like 1_2322
        sector =`2_${res?.data?.private_sector}`
      }
      if (res?.data?.public_sector){
        sector =`1_${res?.data?.public_sector}`
      }

      setSelectedSector({
        sector_select: sector ? [{id: sector, label: getSectorName(sector), name:getSectorName(sector) }] : []
      })
      console.log("sector", sector, getSectorName(sector))

    
    }).finally(response => {
      setLoading(false);
    })
    
    
  }
  
  
  useEffect(() => {
    if (objectId && parseInt(objectId) > -1) {
      getObject(objectId)
    }
  }, [objectId, modalOpen])

  // Load portfolio security dimensions when component mounts or objectId changes
  useEffect(() => {
    if (account?.id && objectId) {
      loadPortfolioSecurityDimensions();
    }
  }, [account?.id, objectId, loadPortfolioSecurityDimensions])
  
  
  if (loading) {
    return (
      <div className="px-3 pt-2">
        <Row className="mt-2 mb-2 border-bottom align-items-center">
          <Col xs={12} md={6} className="pb-3 pl-3">
            <Typography variant="h2" className="mb-0">
              {t("PrivateInvestmentFundEdit_title", "Oma arvopaperi")}
            </Typography>
          </Col>
          <Col xs={12} md={6} className="pb-3 pr-3 d-flex justify-content-end align-items-center">
            <IconButton
              aria-label={t("Close", "Sulje")}
              size="lg"
              onClick={handleReject}
              iconName="times"
              data-dismiss="modal"
            />
          </Col>
        </Row>
        <Container fluid className="py-4">
          <Spinner />
        </Container>
      </div>
    )
  }

  return (
    <PrivateInvestmentFundEditFormik 
      t={t} 
      history={history} 
      preSetValues={preSetValues} 
      notify={notify} 
      objectId={objectId}
      account={account}
      selectedSector={selectedSector}
      setSelectedSector={setSelectedSector}
      portfolioSecurityDimensions={portfolioSecurityDimensions}
      loadPortfolioSecurityDimensions={loadPortfolioSecurityDimensions}
      handleReject={handleReject}
      {...rest} 
    />
  )
  
}

export default PrivateInvestmentFundEditView;
